// Read-only rendering of a generated Form 1094-C transmittal (aca_1094c row).
// Monthly counts are the engine's per-month figures; nothing is copied from
// current headcount and nothing is editable here (overrides live in the
// Forms tab and are audited).
import type React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Help, TermLabel, Explained, CodeHelp } from "@/pages/employer/aca/shared/Help";

export interface Form1094CTransmittal {
  id?: number;
  year: number;
  version: number;
  authoritative?: string | null;
  isAle?: boolean | null;
  isAggregatedGroup?: boolean | null;
  totalForms1095c?: number | null;
  monthlyFtCount?: Record<string, number> | null;
  monthlyTotalCount?: Record<string, number> | null;
  monthlyMecOffered?: Record<string, boolean> | null;
  data?: {
    ale?: { averageFte?: number; basisYear?: number; estimated?: boolean; source?: string; overrideReason?: string | null } | null;
    employeeCountDay?: string;
    selfInsured?: boolean;
  } | null;
  generatedAt?: string | null;
}

interface ClientLike {
  id: number;
  name: string;
  ein?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  contactPhone?: string | null;
  phone?: string | null;
  primaryContactName?: string | null;
}

interface Form1094CProps {
  transmittal: Form1094CTransmittal;
  client: ClientLike;
  taxYear: number;
  members?: Array<{ id?: number; memberName: string; memberEin?: string | null }>;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function Field({ label, value, className = "", help }: { label: string; value?: string | null; className?: string; help?: React.ReactNode }) {
  return (
    <div className={`border rounded p-2 ${className}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1">{label}{help}</div>
      <div className="text-sm font-medium min-h-[1.25rem]">{value || <span className="text-muted-foreground">—</span>}</div>
    </div>
  );
}

export function Form1094C({ transmittal, client, taxYear, members = [] }: Form1094CProps) {
  const ft = transmittal.monthlyFtCount || {};
  const total = transmittal.monthlyTotalCount || {};
  const mec = transmittal.monthlyMecOffered || {};
  const ale = transmittal.data?.ale;
  const mecVals = MONTHS.map((_, i) => (mec[String(i + 1)] ? "X" : ""));
  const ftVals = MONTHS.map((_, i) => (ft[String(i + 1)] == null ? "" : String(ft[String(i + 1)])));
  const totVals = MONTHS.map((_, i) => (total[String(i + 1)] == null ? "" : String(total[String(i + 1)])));
  const aggVals = MONTHS.map(() => (transmittal.isAggregatedGroup ? "X" : ""));
  const same = (arr: string[]) => arr.every((v) => v === arr[0]);
  const rows: Array<{ label: string; vals: string[]; term: "mecOfferIndicator" | "ftCount1094" | "totalCount1094" | "aggregatedAleGroup"; extra?: "employeeCountDay" }> = [
    { label: "(a) Minimum Essential Coverage Offer Indicator", vals: mecVals, term: "mecOfferIndicator" },
    { label: "(b) Full-Time Employee Count", vals: ftVals, term: "ftCount1094" },
    { label: "(c) Total Employee Count", vals: totVals, term: "totalCount1094", extra: "employeeCountDay" },
    { label: "(d) Aggregated Group Indicator", vals: aggVals, term: "aggregatedAleGroup" },
  ];
  const aleText =
    transmittal.isAle == null
      ? "Not determined"
      : transmittal.isAle
        ? `ALE — average ${ale?.averageFte ?? "—"} FT + FTE in ${ale?.basisYear ?? taxYear - 1}${ale?.estimated ? " (estimated)" : ""}${ale?.source === "override" ? " (override)" : ""}`
        : `Not an ALE — average ${ale?.averageFte ?? "—"} FT + FTE in ${ale?.basisYear ?? taxYear - 1}${ale?.estimated ? " (estimated)" : ""}`;

  return (
    <Card className="border-2 border-black print:border">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-xl">Form 1094-C</CardTitle>
            <p className="text-xs text-muted-foreground">Transmittal of Employer-Provided Health Insurance Offer and Coverage Information Returns · Tax year {taxYear}</p>
          </div>
          <div className="flex items-center gap-2">
            <Explained term="snapshotVersion"><Badge variant="outline">v{transmittal.version}</Badge></Explained>
            {transmittal.authoritative === "yes" && <Explained term="authoritativeTransmittal"><Badge variant="secondary">Authoritative transmittal</Badge></Explained>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <section>
          <div className="bg-muted px-2 py-1 text-xs font-semibold"><TermLabel term="form1094c">Part I — Applicable Large Employer Member (ALE Member)</TermLabel></div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-2">
            <Field className="md:col-span-3" label="1 Name of ALE Member (Employer)" value={client.name} />
            <Field className="md:col-span-3" label="2 Employer identification number (EIN)" value={client.ein} />
            <Field className="md:col-span-3" label="3 Street address" value={[client.address1, client.address2].filter(Boolean).join(", ")} />
            <Field label="4 City or town" value={client.city} />
            <Field label="5 State" value={client.state} />
            <Field label="6 ZIP" value={client.zipCode} />
            <Field className="md:col-span-3" label="7 Name of person to contact" value={client.primaryContactName} />
            <Field className="md:col-span-3" label="8 Contact telephone number" value={client.contactPhone ?? client.phone} />
            <Field className="md:col-span-3" label="18 Total number of Forms 1095-C submitted with this transmittal" value={String(transmittal.totalForms1095c ?? 0)} help={<Help term="line18" />} />
            <Field className="md:col-span-3" label="19 Authoritative transmittal" value={transmittal.authoritative === "yes" ? "Yes" : "No"} help={<Help term="authoritativeTransmittal" />} />
          </div>
        </section>

        <section>
          <div className="bg-muted px-2 py-1 text-xs font-semibold"><TermLabel term="ale">Part II — ALE Member Information</TermLabel></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
            <Field label="20 Total number of Forms 1095-C filed by and/or on behalf of ALE Member" value={String(transmittal.totalForms1095c ?? 0)} help={<Help term="line18" />} />
            <Field label="21 Member of an Aggregated ALE Group" value={transmittal.isAggregatedGroup ? "Yes" : "No"} help={<Help term="aggregatedAleGroup" />} />
            <Field label="ALE status (§4980H)" value={aleText} help={<><Help term="ale" />{ale?.estimated && <Help term="aleEstimated" />}</>} />
          </div>
          {ale?.overrideReason && (
            <p className="text-xs text-muted-foreground mt-1">ALE status overridden: {ale.overrideReason}</p>
          )}
        </section>

        <section>
          <div className="bg-muted px-2 py-1 text-xs font-semibold"><TermLabel term="ftCount1094">Part III — ALE Member Information — Monthly</TermLabel></div>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs border">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left p-1 w-56"></th>
                  <th className="p-1"><TermLabel term="all12Months">All 12 months</TermLabel></th>
                  {MONTHS.map((m) => <th key={m} className="p-1">{m}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const all = same(r.vals) ? r.vals[0] : "";
                  return (
                    <tr key={r.label} className="border-t">
                      <td className="p-1"><TermLabel term={r.term}>{r.label}</TermLabel>{r.extra && <Help term={r.extra} className="ml-1" />}</td>
                      <td className="p-1 text-center font-mono font-semibold">{all}</td>
                      {r.vals.map((v, i) => <td key={i} className="p-1 text-center font-mono">{all ? "" : v}</td>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            Column (b) counts ACA full-time employees (130+ hours of service) excluding employees in a Limited Non-Assessment Period.
            Column (c) counts all employees on the {String(transmittal.data?.employeeCountDay || "first_day").replace(/_/g, " ")} of each month.
          </p>
        </section>

        {transmittal.isAggregatedGroup && (
          <section>
            <div className="bg-muted px-2 py-1 text-xs font-semibold"><TermLabel term="aggregatedAleGroup">Part IV — Other ALE Members of Aggregated ALE Group</TermLabel></div>
            {members.length === 0 ? (
              <p className="text-xs text-muted-foreground mt-1">No group members recorded (Settings → Aggregated ALE group).</p>
            ) : (
              <table className="w-full text-xs border mt-2">
                <thead><tr className="bg-muted"><th className="text-left p-1">Name</th><th className="text-left p-1">EIN</th></tr></thead>
                <tbody>
                  {members.map((m, i) => (
                    <tr key={m.id ?? i} className="border-t"><td className="p-1">{m.memberName}</td><td className="p-1 font-mono">{m.memberEin || "—"}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}
      </CardContent>
    </Card>
  );
}
